import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Empty,
  EmployeePicker,
  ErrorBanner,
  GrowingTextarea,
  Loading,
  PageHead,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { formatDate, todayLocal } from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  Employee,
  TrainingRequest,
  TrainingRequestList,
} from '../lib/types';

/**
 * Training Request — the two forms of the Employee Training and Professional
 * Development Policy, filled in on screen and exported to PDF.
 *
 *   Request and approval        before enrolling or spending anything
 *   Completion and reimbursement after the training, to claim and close out
 *
 * The completion form is the policy's own form, field for field. The policy
 * names a "Training Request and Approval Form" but none was supplied, so that
 * one is built from what the policy says it must collect (section 8, step 2)
 * and what the written decision must state (section 8, step 7).
 *
 * THE REQUEST FORM IS NOW SUBMITTED, not only printed (migration 187). The
 * employee is CHOSEN from the directory rather than typed, Request saves it,
 * and it goes to their supervisor's queue on the Requests tab. An approved
 * one lands on that person's People profile under Training. The completion
 * form is unchanged and still prints: reimbursement runs through Finance on
 * paper, and inventing a second workflow for it was not asked for.
 *
 * A DRAFT IS STILL A DRAFT. What is typed stays in this browser until it is
 * submitted or cleared, exactly as before — a half-filled form is nobody's
 * business but the person filling it in. Export to PDF also works as it
 * always has, before or after submitting.
 */

type Values = Record<string, string | string[]>;
type FormKey = 'request' | 'completion';
type Tab = FormKey | 'requests' | 'policy';

const STORAGE: Record<FormKey, string> = {
  request: 'training-request-draft',
  completion: 'training-completion-draft',
};

function loadDraft(key: FormKey): Values {
  try {
    const raw = localStorage.getItem(STORAGE[key]);
    return raw ? (JSON.parse(raw) as Values) : {};
  } catch {
    return {};
  }
}

function saveDraft(key: FormKey, values: Values) {
  try {
    localStorage.setItem(STORAGE[key], JSON.stringify(values));
  } catch {
    /* a private window or blocked storage: the form still works, it just
       will not survive a refresh */
  }
}

/** A money or hours box read as a number; blank and junk count as nothing. */
const num = (v: string | string[] | undefined) => {
  const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function TrainingRequestPage() {
  const [tab, setTab] = useState<Tab>('request');
  const [forms, setForms] = useState<Record<FormKey, Values>>(() => ({
    request: loadDraft('request'),
    completion: loadDraft('completion'),
  }));

  /* Active staff only. A request for somebody who has left is not a request
     anybody wants to approve, and the list is long enough already. */
  const employees = useResource<Employee[]>('/employees?status=active');
  const requests = useResource<TrainingRequestList>('/training-requests');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /* Cleared when the form is edited again, so the confirmation belongs to
     what is on screen rather than to whatever was sent twenty minutes ago. */
  const [submitted, setSubmitted] = useState<TrainingRequest | null>(null);

  useEffect(() => saveDraft('request', forms.request), [forms.request]);
  useEffect(() => saveDraft('completion', forms.completion), [forms.completion]);

  const formKey: FormKey | null = tab === 'policy' || tab === 'requests' ? null : tab;

  const waiting = (requests.data?.requests ?? []).filter(
    (r) => r.Status === 'submitted' && r.CanDecide,
  ).length;

  const submit = () => {
    const v = forms.request;
    const employeeId = Number(str(v, 'employeeId'));
    if (!employeeId) {
      setSubmitError('Choose who the training is for.');
      return;
    }
    if (str(v, 'program').trim() === '') {
      setSubmitError('Name the program, course or credential in section B.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    api
      .post<TrainingRequest>('/training-requests', {
        employeeId,
        program: str(v, 'program').trim(),
        provider: str(v, 'provider'),
        startDate: str(v, 'startDate'),
        targetDate: str(v, 'targetDate'),
        delivery: str(v, 'delivery'),
        examIncluded: str(v, 'examIncluded') === 'Yes',
        examName: str(v, 'examName'),
        examDate: str(v, 'examDate'),
        requestedCategory: str(v, 'requestedCategory'),
        objective: str(v, 'objective'),
        businessUse: str(v, 'businessUse'),
        alternativesNotes: str(v, 'alternativesNotes'),
        knowledgeShare: str(v, 'knowledgeShare'),
        paymentPreference: str(v, 'paymentPreference'),
        renewalTerms: str(v, 'renewalTerms'),
        regularHours: str(v, 'regularHours'),
        coverage: str(v, 'coverage'),
        overnightTravel: str(v, 'overnightTravel') === 'Yes',
        renewing: str(v, 'renewing') === 'Yes',
        repayment: str(v, 'repayment') === 'Yes',
        outsideBudget: str(v, 'outsideBudget'),
        exception: str(v, 'exception') === 'Yes',
        exceptionReason: str(v, 'exceptionReason'),
        /* Only the rows with a figure on them. A blank cost line is not a
           cost of nothing, it is a line the employee did not fill in. */
        costs: COST_ROWS.filter(([k]) => num(v[k]) > 0).map(([k]) => ({
          code: k,
          amount: num(v[k]),
          notes: str(v, `${k}Notes`),
        })),
        hours: HOUR_ROWS.filter(([k]) => num(v[k]) > 0).map(([k]) => ({
          code: k,
          amount: num(v[k]),
          notes: str(v, `${k}Notes`),
        })),
        choices: {
          attachments: list(v, 'attachments'),
          connection: list(v, 'connection'),
          alternatives: list(v, 'alternatives'),
          eligibility: list(v, 'eligibility'),
        },
      })
      .then((saved) => {
        setSubmitted(saved);
        requests.reload();
        /* The draft has become a record. Leaving it in the browser is how
           somebody submits the same request four times. */
        setForms((f) => ({ ...f, request: {} }));
        setTab('requests');
      })
      .catch((err: unknown) => {
        setSubmitError(
          err instanceof ApiError ? err.message : 'The request could not be sent. Try again.',
        );
      })
      .finally(() => setSubmitting(false));
  };

  const clear = () => {
    if (!formKey) return;
    if (!window.confirm('Clear every field on this form? This cannot be undone.')) return;
    setForms((f) => ({ ...f, [formKey]: {} }));
  };

  /* The PDF's file name comes from the document title, so it says which form
     and whose it is rather than "MSP-OPS". */
  const exportPdf = () => {
    if (!formKey) return;
    const who = String(forms[formKey].employeeName ?? '').trim();
    const what = formKey === 'request' ? 'Training Request' : 'Training Completion';
    const previous = document.title;
    document.title = who ? `${what} - ${who}` : what;
    window.print();
    document.title = previous;
  };

  return (
    <>
      <div className="no-print">
        <PageHead
          title="Training Request"
          lede="Request training before you enroll. Choose who it is for, fill in the form, and press Request — it goes to that person's supervisor to approve, and an approved one lands on their People profile under Training. Your draft stays in this browser until you send it or clear it."
          actions={
            formKey ? (
              <>
                <button type="button" className="btn btn-ghost" onClick={clear}>
                  Clear form
                </button>
                <button type="button" className="btn btn-ghost" onClick={exportPdf}>
                  Export to PDF
                </button>
                {formKey === 'request' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submit}
                    disabled={submitting}
                  >
                    {submitting ? 'Sending…' : 'Request'}
                  </button>
                )}
              </>
            ) : undefined
          }
        />

        <div className="tabs">
          <button
            type="button"
            className={`tab${tab === 'request' ? ' active' : ''}`}
            onClick={() => setTab('request')}
          >
            1. Request and approval
          </button>
          <button
            type="button"
            className={`tab${tab === 'completion' ? ' active' : ''}`}
            onClick={() => setTab('completion')}
          >
            2. Completion and reimbursement
          </button>
          <button
            type="button"
            className={`tab${tab === 'requests' ? ' active' : ''}`}
            onClick={() => setTab('requests')}
          >
            Requests{waiting > 0 ? ` (${waiting})` : ''}
          </button>
          <button
            type="button"
            className={`tab${tab === 'policy' ? ' active' : ''}`}
            onClick={() => setTab('policy')}
          >
            Policy summary
          </button>
        </div>
        {formKey === 'request' && (
          <p className="xs muted mb-4">
            <strong>Request</strong> sends this to the supervisor on the chosen employee's record.
            Export to PDF opens the print dialog — choose <strong>Save as PDF</strong> as the
            printer — and works whether or not the request has been sent.
          </p>
        )}
        {formKey === 'completion' && (
          <p className="xs muted mb-4">
            This form is not submitted in the app — export it to PDF and send it on. Export opens
            the print dialog; choose <strong>Save as PDF</strong> as the printer.
          </p>
        )}
        {submitError && (
          <div className="mb-4">
            <ErrorBanner message={submitError} />
          </div>
        )}
      </div>

      {tab === 'request' && (
        <RequestForm
          values={forms.request}
          set={(patch) => {
            setSubmitted(null);
            setSubmitError(null);
            setForms((f) => ({ ...f, request: { ...f.request, ...patch } }));
          }}
          employees={employees.data ?? []}
        />
      )}
      {tab === 'requests' && (
        <RequestsTab
          list={requests}
          justSubmitted={submitted}
          onOpenForm={() => setTab('request')}
        />
      )}
      {tab === 'completion' && (
        <CompletionForm
          values={forms.completion}
          set={(patch) => setForms((f) => ({ ...f, completion: { ...f.completion, ...patch } }))}
        />
      )}
      {tab === 'policy' && <PolicySummary />}
    </>
  );
}

/* =========================================================================
   Building blocks — every field reads and writes one key of the form's values
   ========================================================================= */

interface Bind {
  values: Values;
  set: (patch: Values) => void;
}

const str = (v: Values, k: string) => String(v[k] ?? '');
const list = (v: Values, k: string) => (Array.isArray(v[k]) ? (v[k] as string[]) : []);

function Doc({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <article className="tr-doc">
      <header className="tr-doc-head">
        <div className="tr-doc-org">Denver Tech, LLC</div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </header>
      {children}
      <footer className="tr-doc-foot print-only">
        Employee Training and Professional Development Policy · exported {formatDate(new Date().toISOString())}
      </footer>
    </article>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="tr-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <div className="tr-note">{children}</div>;
}

function Text({
  b,
  k,
  label,
  type = 'text',
  prefix,
  placeholder,
}: {
  b: Bind;
  k: string;
  label: string;
  type?: 'text' | 'date' | 'number';
  prefix?: string;
  placeholder?: string;
}) {
  return (
    <label className="tr-field">
      <span className="tr-label">{label}</span>
      <span className="tr-input-wrap">
        {prefix && <span className="tr-prefix">{prefix}</span>}
        <input
          className="input"
          type={type}
          inputMode={prefix === '$' ? 'decimal' : undefined}
          placeholder={placeholder}
          value={str(b.values, k)}
          /* Lets print hide an empty date's "mm/dd/yyyy" — on paper it should
             be a blank line to write on, like every other empty field. */
          data-empty={str(b.values, k) === '' ? 'true' : undefined}
          onChange={(e) => b.set({ [k]: e.target.value })}
        />
      </span>
    </label>
  );
}

function Long({ b, k, label, placeholder }: { b: Bind; k: string; label: string; placeholder?: string }) {
  return (
    <label className="tr-field tr-field-wide">
      <span className="tr-label">{label}</span>
      <GrowingTextarea
        placeholder={placeholder}
        value={str(b.values, k)}
        onChange={(e) => b.set({ [k]: e.target.value })}
      />
    </label>
  );
}

/**
 * The form's ☐ rows. `multiple` where the paper form lets you tick several
 * (evidence attached); otherwise one answer, and ticking it again clears it.
 */
function Choice({
  b,
  k,
  label,
  options,
  multiple = false,
}: {
  b: Bind;
  k: string;
  label: string;
  options: string[];
  multiple?: boolean;
}) {
  const picked = multiple ? list(b.values, k) : [str(b.values, k)].filter(Boolean);
  const toggle = (o: string) => {
    if (multiple) {
      b.set({ [k]: picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o] });
    } else {
      b.set({ [k]: picked.includes(o) ? '' : o });
    }
  };
  return (
    <div className="tr-field tr-field-wide">
      <span className="tr-label">{label}</span>
      <div className="tr-choices" role={multiple ? 'group' : 'radiogroup'} aria-label={label}>
        {options.map((o) => (
          <label key={o} className={`tr-choice${picked.includes(o) ? ' on' : ''}`}>
            <input
              type="checkbox"
              checked={picked.includes(o)}
              onChange={() => toggle(o)}
            />
            <span>{o}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return <div className="tr-grid">{children}</div>;
}

function Signature({ b, k, label }: { b: Bind; k: string; label: string }) {
  return (
    <Grid>
      <Text b={b} k={`${k}Name`} label={`${label} — type full name as signature`} />
      <Text b={b} k={`${k}Date`} label="Date" type="date" />
    </Grid>
  );
}

/* =========================================================================
   1. Training Request and Approval
   ========================================================================= */

const COST_ROWS: [string, string][] = [
  ['courseCost', 'Course / registration'],
  ['materialsCost', 'Required materials'],
  ['examCost', 'Initial exam attempt'],
  ['labCost', 'Lab / practice exam'],
  ['travelCost', 'Travel (only if approved in advance)'],
  ['otherCost', 'Subscription / other'],
];

const HOUR_ROWS: [string, string][] = [
  ['hoursInstruction', 'Instructional'],
  ['hoursStudy', 'Self-study'],
  ['hoursLab', 'Lab'],
  ['hoursPractice', 'Practice exam'],
  ['hoursExam', 'Exam'],
  ['hoursTravel', 'Travel'],
];

function RequestForm({ employees, ...b }: Bind & { employees: Employee[] }) {
  const v = b.values;
  const chosen = employees.find((e) => e.EmployeeId === Number(str(v, 'employeeId'))) ?? null;
  const totalCost = COST_ROWS.reduce((s, [k]) => s + num(v[k]), 0);
  const totalHours = HOUR_ROWS.reduce((s, [k]) => s + num(v[k]), 0);

  /* Section 8, step 6: any one of these needs the Company Owner too. */
  const triggers = useMemo(() => {
    const t: string[] = [];
    if (totalCost > 1000) t.push(`total cost is ${money(totalCost)}, over $1,000`);
    if (totalHours > 20) t.push(`${totalHours} hours requested, over 20 paid work hours`);
    if (str(v, 'overnightTravel') === 'Yes') t.push('overnight travel');
    if (str(v, 'renewing') === 'Yes') t.push('a multi-year or automatically renewing commitment');
    if (str(v, 'repayment') === 'Yes') t.push('a repayment agreement');
    if (str(v, 'exception') === 'Yes') t.push('an exception to the policy');
    if (str(v, 'outsideBudget') === 'Yes') t.push('outside an established training budget');
    return t;
  }, [totalCost, totalHours, v]);

  return (
    <Doc
      title="Training Request and Approval Form"
      subtitle="Complete before enrolling, paying, or committing to any training. Attach the provider's course description, schedule, price, refund terms, completion requirements, and exam details when applicable."
    >
      <Note>
        A decision is normally issued within 10 business days of a <strong>complete</strong>{' '}
        request. Silence is not approval, and managers may not promise reimbursement or paid time
        before final written approval. Approval expires on the date stated in the decision, or 45
        calendar days after approval if none is stated.
      </Note>

      <Section title="A. Employee Information">
        <Grid>
          <label className="tr-field">
            <span className="tr-label">Employee</span>
            {/* CHOSEN, NOT TYPED. The request belongs to an EmployeeId —
                that is what routes it to a supervisor and files it on the
                right profile afterwards. A typed name does neither, and two
                spellings of one person become two people. */}
            <EmployeePicker
              employees={employees}
              value={chosen?.EmployeeId ?? null}
              placeholder="Choose the employee"
              onChange={(id) => {
                const picked = employees.find((e) => e.EmployeeId === id) ?? null;
                /* The three fields beside it are the directory's answers, not
                   the employee's — filled from the record so the printed form
                   says what the app will act on. */
                b.set({
                  employeeId: id === null ? '' : String(id),
                  employeeName: picked?.FullName ?? '',
                  jobTitle: picked?.RoleTitle ?? '',
                  manager: picked?.ManagerName ?? '',
                  hireDate: picked?.StartDate ? picked.StartDate.slice(0, 10) : '',
                  dateSubmitted: str(b.values, 'dateSubmitted') || todayLocal(),
                });
              }}
            />
          </label>
          <Text b={b} k="jobTitle" label="Job title / role" />
          <Text b={b} k="manager" label="Manager (approves this request)" />
          <Text b={b} k="hireDate" label="Date of hire" type="date" />
          <Text b={b} k="dateSubmitted" label="Date submitted" type="date" />
        </Grid>
        {chosen && !chosen.ManagerEmployeeId && (
          <div className="tr-flag on">
            <strong>No supervisor on {chosen.FullName}'s record.</strong> The request can still be
            sent — it goes to the app administrators to decide instead. Setting their manager on
            the Employees page is what routes it to the right person.
          </div>
        )}
      </Section>

      <Section title="B. Training Requested">
        <Grid>
          <Text b={b} k="program" label="Program / course / credential" />
          <Text b={b} k="provider" label="Provider" />
          <Text b={b} k="startDate" label="Start date" type="date" />
          <Text b={b} k="targetDate" label="Target completion date" type="date" />
        </Grid>
        <Choice
          b={b}
          k="delivery"
          label="Delivery"
          options={['In person', 'Online, instructor-led', 'Online, self-paced', 'Conference / workshop', 'Exam only', 'Subscription']}
        />
        <Choice b={b} k="examIncluded" label="Certification exam included" options={['Yes', 'No']} />
        {str(v, 'examIncluded') === 'Yes' && (
          <Grid>
            <Text b={b} k="examName" label="Exam" />
            <Text b={b} k="examDate" label="Planned exam date" type="date" />
          </Grid>
        )}
        <Choice
          b={b}
          k="requestedCategory"
          label="Requested category (Denver Tech makes the final classification)"
          options={['Company-required', 'Company-sponsored role development', 'Employee-requested voluntary development']}
        />
        <Choice
          b={b}
          k="attachments"
          label="Attached from the provider"
          multiple
          options={['Course description', 'Schedule', 'Price', 'Refund terms', 'Completion requirements', 'Exam details']}
        />
      </Section>

      <Section title="C. Business Case">
        <Long b={b} k="objective" label="Development objective and skill gap" />
        <Long b={b} k="businessUse" label="How it will be used at Denver Tech, and the expected result" />
        <Choice
          b={b}
          k="connection"
          label="Connection to the business"
          multiple
          options={['Current duties', 'Approved development path', 'Supported technology', 'Service catalog', 'Client need', 'Documented skills gap']}
        />
        <Choice
          b={b}
          k="alternatives"
          label="Alternatives considered"
          multiple
          options={['Internal resources', 'Vendor-provided training', 'Existing subscriptions', 'Mentoring / labs', 'Lower-cost option']}
        />
        <Long b={b} k="alternativesNotes" label="Why the alternatives do not meet the need" />
        <Long
          b={b}
          k="knowledgeShare"
          label="How the knowledge could be shared (within course-licence and copyright limits)"
        />
      </Section>

      <Section title="D. Costs Requested">
        <table className="tr-table">
          <thead>
            <tr>
              <th>Cost item</th>
              <th style={{ width: '24%' }}>Amount</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {COST_ROWS.map(([k, label]) => (
              <tr key={k}>
                <td>{label}</td>
                <td>
                  <MoneyCell b={b} k={k} />
                </td>
                <td>
                  <CellInput b={b} k={`${k}Notes`} />
                </td>
              </tr>
            ))}
            <tr className="tr-total">
              <td>TOTAL REQUESTED</td>
              <td>{money(totalCost)}</td>
              <td />
            </tr>
          </tbody>
        </table>
        <Choice
          b={b}
          k="paymentPreference"
          label="Payment arrangement requested"
          options={['Direct Company payment', 'Employee reimbursement']}
        />
        <Choice b={b} k="renewing" label="Multi-year or automatically renewing commitment" options={['Yes', 'No']} />
        {str(v, 'renewing') === 'Yes' && (
          <Long b={b} k="renewalTerms" label="Subscription term, renewal treatment and cancellation responsibility" />
        )}
      </Section>

      <Section title="E. Time Requested">
        <Note>
          Approval of program cost and approval of paid time are separate decisions. List the
          hours you are asking to be paid for each part.
        </Note>
        <table className="tr-table">
          <thead>
            <tr>
              <th>Component</th>
              <th style={{ width: '24%' }}>Hours requested</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {HOUR_ROWS.map(([k, label]) => (
              <tr key={k}>
                <td>{label}</td>
                <td>
                  <CellInput b={b} k={k} numeric />
                </td>
                <td>
                  <CellInput b={b} k={`${k}Notes`} />
                </td>
              </tr>
            ))}
            <tr className="tr-total">
              <td>TOTAL HOURS</td>
              <td>{totalHours}</td>
              <td />
            </tr>
          </tbody>
        </table>
        <Choice
          b={b}
          k="regularHours"
          label="Scheduled during regular working hours"
          options={['Yes', 'Partly', 'No']}
        />
        <Choice b={b} k="overnightTravel" label="Overnight travel required" options={['Yes', 'No']} />
        <Long b={b} k="coverage" label="Help-desk, escalation, project, on-call and client coverage plan" />
      </Section>

      <Section title="F. Eligibility and Approval Triggers">
        <Choice
          b={b}
          k="eligibility"
          label="Employee confirms"
          multiple
          options={[
            'Actively employed and in good standing',
            'Employed 90 days or more (or a waiver is requested below)',
            'Current on Company-required training',
            'Discussed the objective with my manager',
          ]}
        />
        <Choice b={b} k="exception" label="Requesting an exception to the policy (including a 90-day waiver)" options={['Yes', 'No']} />
        {str(v, 'exception') === 'Yes' && <Long b={b} k="exceptionReason" label="Exception requested and why" />}
        <Choice b={b} k="repayment" label="Includes a repayment agreement" options={['Yes', 'No']} />
        <Choice b={b} k="outsideBudget" label="Outside an established training budget" options={['Yes', 'No', 'Not known']} />
        <div className={`tr-flag${triggers.length > 0 ? ' on' : ''}`}>
          {triggers.length > 0 ? (
            <>
              <strong>Company Owner approval required</strong> — {triggers.join('; ')}.
            </>
          ) : (
            <>No Company Owner approval trigger from what is entered so far. Routine requests need the manager and the COO.</>
          )}
        </div>
        <p className="tr-cert">
          I confirm the information above is accurate, I will not enroll, pay, or commit to this
          training before written approval, and I will notify my manager before any change to the
          provider, course, schedule, cost, paid hours or travel.
        </p>
        <Signature b={b} k="employeeSig" label="Employee" />
      </Section>

      <Section title="G. Manager Recommendation">
        <Choice
          b={b}
          k="mgrRecommendation"
          label="Recommendation"
          options={['Approve', 'Conditionally approve', 'Defer', 'Deny']}
        />
        <Choice b={b} k="mgrGoodStanding" label="Good standing confirmed (job-related, supportable)" options={['Yes', 'No']} />
        <Choice b={b} k="mgrCoverage" label="Coverage confirmed" options={['Yes', 'No']} />
        <Long b={b} k="mgrReason" label="Conditions, or the specific reason for a deferral or denial" />
        <Signature b={b} k="mgrSig" label="Manager" />
      </Section>

      <Section title="H. Written Decision — COO / Policy Administrator">
        <Choice
          b={b}
          k="finalCategory"
          label="Final category"
          options={['Company-required', 'Company-sponsored role development', 'Employee-requested voluntary development', 'Personal enrichment (not supported)']}
        />
        <Choice b={b} k="decision" label="Decision" options={['Approved', 'Conditionally approved', 'Deferred', 'Denied']} />
        <Grid>
          <Text b={b} k="approvedMax" label="Approved maximum" prefix="$" />
          <Text b={b} k="approvedPayment" label="Payment method" placeholder="Direct payment / reimbursement" />
          <Text b={b} k="approvalExpires" label="Approval expires" type="date" />
          <Text b={b} k="completionDeadline" label="Completion deadline" type="date" />
        </Grid>
        <Long b={b} k="approvedItems" label="Approved cost items" />
        <Long b={b} k="approvedSchedule" label="Approved schedule" />
        <Long
          b={b}
          k="approvedHours"
          label="Maximum paid hours by component (instructional, self-study, lab, practice exam, exam, travel) and timekeeping instructions"
        />
        <Long b={b} k="completionStandard" label="Completion standard and evidence required" />
        <Long b={b} k="knowledgeTransfer" label="Knowledge-transfer obligation, if any" />
        <Signature b={b} k="cooSig" label="COO" />
        <Choice b={b} k="ownerNeeded" label="Company Owner approval" options={['Required', 'Not required']} />
        {str(v, 'ownerNeeded') === 'Required' && <Signature b={b} k="ownerSig" label="Company Owner" />}
      </Section>
    </Doc>
  );
}

/* =========================================================================
   2. Training Completion and Reimbursement — the policy's form, as written
   ========================================================================= */

const REIMBURSE_ROWS: [string, string][] = [
  ['rCourse', 'Course / registration'],
  ['rMaterials', 'Required materials'],
  ['rExam', 'Initial exam attempt'],
  ['rLab', 'Lab / practice exam'],
  ['rTravel', 'Approved travel'],
  ['rOther', 'Subscription / other'],
];

function CompletionForm(b: Bind) {
  const v = b.values;
  const paidAmount = REIMBURSE_ROWS.reduce((s, [k]) => s + num(v[k]), 0);
  const paidTax = REIMBURSE_ROWS.reduce((s, [k]) => s + num(v[`${k}Tax`]), 0);
  const totalPaid = paidAmount + paidTax;
  const net = Math.max(0, totalPaid - num(v.refunds) - num(v.thirdParty));

  return (
    <Doc
      title="Training Completion and Reimbursement Form"
      subtitle="Use after completion when reimbursement, completion verification, credential recording, or knowledge-transfer follow-up is required."
    >
      <Note>
        Submit within 30 calendar days after completion or by the earlier deadline stated in the
        written approval. A complete and approved reimbursement submission is normally processed
        within two regular payroll cycles after Finance has all required documentation and any
        discrepancy is resolved.
      </Note>

      <Section title="A. Employee and Approval Information">
        <Grid>
          <Text b={b} k="employeeName" label="Employee name" />
          <Text b={b} k="manager" label="Manager" />
          <Text b={b} k="program" label="Program / credential" />
          <Text b={b} k="provider" label="Provider" />
        </Grid>
        <Choice
          b={b}
          k="category"
          label="Approved category"
          options={['Company-required', 'Company-sponsored role development', 'Employee-requested voluntary development']}
        />
        <Choice b={b} k="payment" label="Payment arrangement" options={['Direct Company payment', 'Employee reimbursement']} />
        <Grid>
          <Text b={b} k="approvalDate" label="Approval date" type="date" />
          <Text b={b} k="approvalExpires" label="Approval expiration date" type="date" />
          <Text b={b} k="approvedMax" label="Approved maximum" prefix="$" />
          <Text b={b} k="completionDeadline" label="Completion deadline" type="date" />
          <Text b={b} k="submissionDeadline" label="Submission deadline" type="date" />
        </Grid>
        <Long b={b} k="approvedStandard" label="Approved completion / reimbursement standard" />
      </Section>

      <Section title="B. Completion Evidence">
        <Grid>
          <Text b={b} k="completionDate" label="Completion / exam date" type="date" />
        </Grid>
        <Choice b={b} k="courseCompleted" label="Course completed" options={['Yes', 'No', 'Not applicable']} />
        <Choice b={b} k="examAttended" label="Exam attended" options={['Yes', 'No', 'Not applicable']} />
        <Choice b={b} k="examResult" label="Exam result" options={['Passed', 'Not passed', 'Pending', 'Not applicable']} />
        <Choice b={b} k="credentialIssued" label="Credential issued" options={['Yes', 'No', 'Not applicable']} />
        <Choice
          b={b}
          k="evidence"
          label="Evidence attached"
          multiple
          options={['Certificate', 'Transcript', 'Attendance / provider confirmation', 'Passing score', 'Credential record', 'Other']}
        />
        <Long
          b={b}
          k="explanation"
          label="Explanation if the approved standard was not met, completion was late, or an exception is requested"
        />
        <Long b={b} k="knowledgeGained" label="Knowledge or skill gained and application to Denver Tech work" />
        <Choice b={b} k="ktStatus" label="Knowledge transfer" options={['Completed', 'Scheduled', 'Not required']} />
        <Long b={b} k="ktDeliverable" label="Knowledge-transfer deliverable or scheduled date" />
      </Section>

      <Section title="C. Employee Reimbursement Request, if applicable">
        <Note>
          Skip this section if Denver Tech paid all approved costs directly and no employee
          reimbursement is requested.
        </Note>
        <table className="tr-table">
          <thead>
            <tr>
              <th>Cost item</th>
              <th style={{ width: '18%' }}>Amount</th>
              <th style={{ width: '18%' }}>Tax / fee</th>
              <th>Receipt / notes</th>
            </tr>
          </thead>
          <tbody>
            {REIMBURSE_ROWS.map(([k, label]) => (
              <tr key={k}>
                <td>{label}</td>
                <td>
                  <MoneyCell b={b} k={k} />
                </td>
                <td>
                  <MoneyCell b={b} k={`${k}Tax`} />
                </td>
                <td>
                  <CellInput b={b} k={`${k}Notes`} />
                </td>
              </tr>
            ))}
            <tr className="tr-total">
              <td>TOTAL PAID BY EMPLOYEE</td>
              <td>{money(paidAmount)}</td>
              <td>{money(paidTax)}</td>
              <td>{money(totalPaid)} including tax / fees</td>
            </tr>
          </tbody>
        </table>
        <table className="tr-table tr-table-narrow">
          <tbody>
            <tr>
              <td>Refunds / credits / discounts / vouchers</td>
              <td>
                <MoneyCell b={b} k="refunds" />
              </td>
            </tr>
            <tr>
              <td>Third-party payment</td>
              <td>
                <MoneyCell b={b} k="thirdParty" />
              </td>
            </tr>
            <tr className="tr-total">
              <td>NET AMOUNT REQUESTED</td>
              <td>{money(net)}</td>
            </tr>
          </tbody>
        </table>
        {num(v.approvedMax) > 0 && net > num(v.approvedMax) && (
          <div className="tr-flag on">
            The net amount requested is more than the approved maximum of {money(num(v.approvedMax))}.
            Costs beyond the approved amount are not normally eligible.
          </div>
        )}
        <Long
          b={b}
          k="refundDetails"
          label="Describe any refund, credit, voucher, discount, provider cancellation, or third-party payment"
        />
        <p className="tr-cert">
          I certify that the information and receipts are accurate; all refunds, credits, discounts,
          vouchers, and third-party payments have been disclosed; I am not requesting duplicate
          reimbursement; and the request is limited to costs approved in writing.
        </p>
        <Grid>
          <Text b={b} k="employeeSigName" label="Employee signature — type full name" />
          <Text b={b} k="employeeSigDate" label="Date submitted" type="date" />
        </Grid>
      </Section>

      <Section title="D. Manager Completion Verification">
        <Choice b={b} k="mgrStandardMet" label="Approved completion standard met" options={['Yes', 'No', 'More information needed']} />
        <Choice
          b={b}
          k="mgrKtMet"
          label="Knowledge-transfer requirement met"
          options={['Yes', 'No', 'Scheduled', 'Not required']}
        />
        <Choice
          b={b}
          k="mgrRecommended"
          label="Recommended reimbursement"
          options={['Full approved amount', 'Adjusted amount', 'Do not reimburse', 'Not applicable - direct payment only']}
        />
        {str(v, 'mgrRecommended') === 'Adjusted amount' && (
          <Grid>
            <Text b={b} k="mgrAdjusted" label="Adjusted amount" prefix="$" />
          </Grid>
        )}
        <Long
          b={b}
          k="mgrExplanation"
          label="Required explanation for adjustment, denial, incomplete standard, or exception"
        />
        <Grid>
          <Text b={b} k="mgrSigName" label="Manager name / signature" />
          <Text b={b} k="mgrSigDate" label="Date" type="date" />
        </Grid>
      </Section>

      <Section title="E. COO / Policy Administrator Review">
        <Choice b={b} k="cooSubmission" label="Submission" options={['Complete', 'Incomplete', 'No reimbursement request']} />
        <Choice
          b={b}
          k="cooWithin"
          label="Within written approval"
          options={['Yes', 'No', 'Company Owner exception approved']}
        />
        <Choice
          b={b}
          k="cooTreatment"
          label="Completion treatment"
          options={['Requirement met', 'Exception approved', 'Requirement not met', 'Pending information']}
        />
        <Choice b={b} k="cooCredential" label="Credential / skills record" options={['Updated', 'Not applicable', 'Pending']} />
        <Choice
          b={b}
          k="cooTracking"
          label="Subscription, voucher, credit, or renewal tracking"
          options={['Updated', 'Not applicable', 'Pending']}
        />
        <Grid>
          <Text b={b} k="cooAmount" label="Approved reimbursable amount" prefix="$" />
          <Text b={b} k="cooSigName" label="COO name / signature" />
          <Text b={b} k="cooSigDate" label="Date" type="date" />
        </Grid>
      </Section>

      <Section title="F. Finance Processing">
        <Grid>
          <Text b={b} k="finAmount" label="Final reimbursable amount" prefix="$" />
          <Text b={b} k="finReference" label="Payment / expense reference" />
          <Text b={b} k="finReceived" label="Complete submission received date" type="date" />
          <Text b={b} k="finBy" label="Processed by" />
          <Text b={b} k="finDate" label="Processing date" type="date" />
        </Grid>
        <Choice b={b} k="finTax" label="Tax treatment reviewed" options={['Yes', 'Not applicable']} />
        <Long b={b} k="finNotes" label="Finance notes" />
      </Section>
    </Doc>
  );
}

function CellInput({ b, k, numeric = false }: { b: Bind; k: string; numeric?: boolean }) {
  return (
    <input
      className="input tr-cell"
      inputMode={numeric ? 'decimal' : undefined}
      value={str(b.values, k)}
      onChange={(e) => b.set({ [k]: e.target.value })}
    />
  );
}

function MoneyCell({ b, k }: { b: Bind; k: string }) {
  return (
    <span className="tr-input-wrap">
      <span className="tr-prefix">$</span>
      <CellInput b={b} k={k} numeric />
    </span>
  );
}

/* =========================================================================
   3. Requests — the queue, and the supervisor's decision on it
   ========================================================================= */

/**
 * Everything the person reading may see, in three groups that answer three
 * different questions:
 *
 *   Waiting on you   requests they must decide — first, because it is the
 *                    only group that is somebody's job
 *   Waiting          sent, not yet decided, and not theirs to decide
 *   Decided          approved and denied, newest first
 *
 * An empty queue is said plainly rather than left as a blank panel, because
 * "nothing is waiting on me" and "this did not load" look identical otherwise.
 */
function RequestsTab({
  list,
  justSubmitted,
  onOpenForm,
}: {
  list: ReturnType<typeof useResource<TrainingRequestList>>;
  justSubmitted: TrainingRequest | null;
  onOpenForm: () => void;
}) {
  if (list.loading) return <Loading label="Loading training requests" />;
  if (list.error) return <ErrorBanner message={list.error} />;

  const all = list.data?.requests ?? [];
  const mine = all.filter((r) => r.Status === 'submitted' && r.CanDecide);
  const waiting = all.filter((r) => r.Status === 'submitted' && !r.CanDecide);
  const decided = all.filter((r) => r.Status !== 'submitted');

  return (
    <div className="stack">
      {justSubmitted && (
        <div className="tr-sent">
          <strong>Request sent.</strong> {justSubmitted.Program} for {justSubmitted.EmployeeName}{' '}
          {justSubmitted.SupervisorName
            ? `is with ${justSubmitted.SupervisorName} to approve.`
            : 'has no supervisor on the record, so it is with the app administrators to decide.'}
        </div>
      )}

      {/* Somebody signed in without a record in the directory. They may still
          raise a request for anybody on it, but nothing will ever arrive in
          their own queue, and an empty "Waiting on you" reads as "nobody has
          asked you for anything" rather than "you are not in the line". */}
      {list.data && list.data.viewerEmployeeId === null && !list.data.isAdmin && (
        <div className="tr-flag">
          <strong>You are not in the employee directory.</strong> You can raise a request for
          anybody on it, but approvals go to the supervisor on the chosen person's record, so
          nothing will wait on you here.
        </div>
      )}

      {all.length === 0 ? (
        <Empty title="No training requests yet">
          <p className="muted">
            Fill in the request form and press Request — it appears here, and goes to the chosen
            employee's supervisor.
          </p>
          <button type="button" className="btn btn-primary" onClick={onOpenForm}>
            Open the request form
          </button>
        </Empty>
      ) : (
        <>
          <RequestGroup
            title="Waiting on you"
            subtitle="You are the supervisor on these. Approving one puts it on that person's People profile under Training."
            requests={mine}
            emptyText="Nothing is waiting on your approval."
            onChanged={list.reload}
          />
          <RequestGroup
            title="Waiting"
            subtitle="Sent, and with somebody else to decide."
            requests={waiting}
            emptyText="Nothing of yours is waiting on a decision."
            onChanged={list.reload}
          />
          <RequestGroup
            title="Decided"
            requests={decided}
            emptyText="Nothing has been decided yet."
            onChanged={list.reload}
          />
        </>
      )}
    </div>
  );
}

function RequestGroup({
  title,
  subtitle,
  requests,
  emptyText,
  onChanged,
}: {
  title: string;
  subtitle?: string;
  requests: TrainingRequest[];
  emptyText: string;
  onChanged: () => void;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h3>
            {title}
            {requests.length > 0 ? ` (${requests.length})` : ''}
          </h3>
          {subtitle && <p className="xs muted">{subtitle}</p>}
        </div>
      </div>
      <div className="card-body">
        {requests.length === 0 ? (
          <p className="muted small">{emptyText}</p>
        ) : (
          <div className="stack">
            {requests.map((r) => (
              <RequestCard key={r.TrainingRequestId} request={r} onChanged={onChanged} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const STATUS_BADGE: Record<TrainingRequest['Status'], string> = {
  submitted: 'badge-warning',
  approved: 'badge-good',
  denied: 'badge-bad',
};

const statusLabel = (s: TrainingRequest['Status']) =>
  s === 'submitted' ? 'Waiting' : s === 'approved' ? 'Approved' : 'Denied';

function RequestCard({
  request: r,
  onChanged,
}: {
  request: TrainingRequest;
  onChanged: () => void;
}) {
  const [deciding, setDeciding] = useState<'approved' | 'denied' | null>(null);

  return (
    <article className="mini-row tr-request">
      <div className="row-between wrap">
        <div>
          <div className="strong">{r.Program}</div>
          <div className="xs muted">
            {r.EmployeeName}
            {r.EmployeeRole ? ` · ${r.EmployeeRole}` : ''}
            {r.Provider ? ` · ${r.Provider}` : ''}
          </div>
        </div>
        <span className={`badge ${STATUS_BADGE[r.Status]}`}>{statusLabel(r.Status)}</span>
      </div>

      <dl className="tr-request-facts">
        <Fact label="Cost">{money(Number(r.TotalCost))}</Fact>
        <Fact label="Hours">{Number(r.TotalHours)}</Fact>
        <Fact label="Starts">{r.StartDate ? formatDate(r.StartDate) : '—'}</Fact>
        <Fact label="Target">{r.TargetDate ? formatDate(r.TargetDate) : '—'}</Fact>
        <Fact label="Supervisor">{r.SupervisorName ?? 'None on record'}</Fact>
        <Fact label="Submitted">{formatDate(r.SubmittedUtc)}</Fact>
      </dl>

      {r.Objective && <p className="small tr-request-note">{r.Objective}</p>}

      {r.Status !== 'submitted' && (
        <div className="xs muted">
          {statusLabel(r.Status)} by {r.DecidedByName ?? 'somebody'}
          {r.DecidedUtc ? ` on ${formatDate(r.DecidedUtc)}` : ''}
          {r.ApprovedAmount !== null ? ` · up to ${money(Number(r.ApprovedAmount))}` : ''}
          {r.ApprovedHours !== null ? ` · ${Number(r.ApprovedHours)} paid hours` : ''}
          {r.DecisionNotes ? ` · ${r.DecisionNotes}` : ''}
        </div>
      )}

      {r.Status === 'submitted' && r.CanDecide && !deciding && (
        <div className="row gap">
          <button type="button" className="btn btn-primary" onClick={() => setDeciding('approved')}>
            Approve
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setDeciding('denied')}>
            Deny
          </button>
        </div>
      )}

      {deciding && (
        <DecisionForm
          request={r}
          decision={deciding}
          onCancel={() => setDeciding(null)}
          onDone={() => {
            setDeciding(null);
            onChanged();
          }}
        />
      )}
    </article>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * The decision.
 *
 * An approval may be for LESS than was asked — the course but not the travel,
 * half the study hours — so both boxes start at what was requested and can be
 * edited down. They are what the profile then shows as approved, which is why
 * they are on the approval rather than in a note somebody has to read.
 *
 * A denial takes the reason and nothing else: an amount on a denial would
 * read as a half-approval on the record.
 */
function DecisionForm({
  request: r,
  decision,
  onCancel,
  onDone,
}: {
  request: TrainingRequest;
  decision: 'approved' | 'denied';
  onCancel: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(String(Number(r.TotalCost)));
  const [hours, setHours] = useState(String(Number(r.TotalHours)));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    setSaving(true);
    setError(null);
    api
      .post(`/training-requests/${r.TrainingRequestId}/decision`, {
        decision,
        approvedAmount: decision === 'approved' ? num(amount) : null,
        approvedHours: decision === 'approved' ? num(hours) : null,
        notes,
      })
      .then(onDone)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'The decision could not be saved.');
        setSaving(false);
      });
  };

  return (
    <div className="tr-decision">
      <p className="xs muted">
        {decision === 'approved'
          ? `Approving puts this on ${r.EmployeeName}'s People profile under Training. A decision cannot be changed afterwards.`
          : 'Say why, so the employee has an answer. A decision cannot be changed afterwards.'}
      </p>
      {decision === 'approved' && (
        <Grid>
          <label className="tr-field">
            <span className="tr-label">Approved maximum</span>
            <span className="tr-input-wrap">
              <span className="tr-prefix">$</span>
              <input
                className="input"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </span>
          </label>
          <label className="tr-field">
            <span className="tr-label">Approved paid hours</span>
            <input
              className="input"
              inputMode="decimal"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </label>
        </Grid>
      )}
      <label className="tr-field tr-field-wide">
        <span className="tr-label">
          {decision === 'approved' ? 'Conditions or notes' : 'Reason for the denial'}
        </span>
        <GrowingTextarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      {error && <ErrorBanner message={error} />}
      <div className="row gap">
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : decision === 'approved' ? 'Approve request' : 'Deny request'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* =========================================================================
   The policy, in brief — for the person filling the forms in
   ========================================================================= */

function PolicySummary() {
  return (
    <article className="tr-doc tr-policy">
      <header className="tr-doc-head">
        <div className="tr-doc-org">Denver Tech, LLC</div>
        <h2>Employee Training and Professional Development Policy — the short version</h2>
        <p>
          The rules that decide what goes on the forms. The full policy governs; the Policy
          Administrator is the Chief Operating Officer.
        </p>
      </header>

      <Section title="The four categories">
        <ul>
          <li>
            <strong>Company-required</strong> — assigned by Denver Tech. The Company pays, the time
            is paid work time, and no request form is needed beyond the written assignment.
          </li>
          <li>
            <strong>Company-sponsored role development</strong> — proposed by the employee or
            manager, approved as a business investment. Paid directly or reimbursed, as the
            approval states.
          </li>
          <li>
            <strong>Employee-requested voluntary development</strong> — the employee's choice, with
            enough Denver Tech value to justify support. Normally employee-paid and reimbursed after
            successful completion; for certifications, passing the exam is the normal condition.
          </li>
          <li>
            <strong>Personal enrichment</strong> — not supported: no payment and no paid time.
          </li>
        </ul>
      </Section>

      <Section title="Before you enroll">
        <ul>
          <li>Talk to your manager first, then complete the request with the provider's details attached.</li>
          <li>
            Optional development normally needs 90 days of employment and good standing, and you must be
            current on required training. The COO can waive the 90 days.
          </li>
          <li>Decisions normally come within 10 business days of a complete request. Silence is not approval.</li>
          <li>Routine requests need the manager and the COO.</li>
          <li>
            The Company Owner must also approve if the request is over $1,000, over 20 paid hours,
            needs overnight travel, renews automatically or runs for years, includes a repayment
            agreement, needs a policy exception, or is outside the training budget.
          </li>
          <li>
            Do not enroll, pay or commit until you have written approval. It expires on the stated
            date, or after 45 calendar days.
          </li>
        </ul>
      </Section>

      <Section title="Costs and time">
        <ul>
          <li>
            Normally eligible: tuition and registration, required materials, one initial exam
            attempt if approved, a lab or practice exam, and travel approved in advance.
          </li>
          <li>
            Normally excluded: unapproved add-ons, late or cancellation fees, exam retakes,
            general equipment, unapproved travel, and subscriptions beyond the learning period.
          </li>
          <li>
            Paid time is a separate decision from cost. Only the hours approved for each component
            are paid, and non-exempt employees must record all time actually worked.
          </li>
        </ul>
      </Section>

      <Section title="After you finish">
        <ul>
          <li>
            Submit the completion form with itemized paid receipts and completion evidence within
            30 calendar days, or by the earlier date in the approval.
          </li>
          <li>Disclose every refund, credit, voucher, discount or third-party payment; reimbursement cannot exceed your net cost.</li>
          <li>A complete submission is normally paid within two regular payroll cycles.</li>
          <li>Leaving the company does not by itself create a repayment obligation.</li>
        </ul>
      </Section>
    </article>
  );
}
